import { atomWithRefresh } from 'jotai/utils';
import { fetchModelsData, safeTransformModel } from '~/service/fetchModelsData';
import { fetchStylesData } from '~/service/fetchStylesData';
import { fetchProjects } from '~/service/projectsService';
import { fetchExpressions } from '~/service/expressionsService';
import { initialFilters } from '~/atoms/filters';
import { atom } from 'jotai';
import log from '~/logger';
import { userPreferencesAtom } from '~/atoms/userPreferences';
import { lookupsAtom } from '~/atoms/lookups';
import { linePlanEntityAtom, productsDataAtom } from '~/atoms/linePlanEntity';
// Style-related type guards and helpers (kept here as styles are not in service layer yet)
const isString = (value) => typeof value === 'string';
const isNumber = (value) => typeof value === 'number';
const isBoolean = (value) => typeof value === 'boolean';
const isStringArray = (value) => Array.isArray(value) && value.every((item) => typeof item === 'string');
const isLinkedStylesArray = (value) => Array.isArray(value) &&
    value.every((item) => typeof item === 'object' &&
        item !== null &&
        'styleNumber' in item &&
        typeof item.styleNumber === 'string' &&
        'type' in item &&
        typeof item.type === 'string');
const getRequiredString = (value) => isString(value) ? value : '';
const getOptionalString = (value) => isString(value) ? value : undefined;
const getOptionalStringArray = (value) => isStringArray(value) ? value : undefined;
const safeTransformStyle = (styleData) => {
    if (!isString(styleData.id)) {
        throw new Error(`Invalid styleNumber: expected string, got ${typeof styleData.id}`);
    }
    if (!isString(styleData.styleName)) {
        throw new Error(`Invalid styleName: expected string, got ${typeof styleData.styleName}`);
    }
    if (!isNumber(styleData.modelId)) {
        throw new Error(`Invalid modelId: expected number, got ${typeof styleData.modelId}`);
    }
    return {
        styleNumber: styleData.id,
        styleName: styleData.styleName,
        modelId: styleData.modelId,
        modificationDate: getRequiredString(styleData.modificationDate),
        modifiedBy: getRequiredString(styleData.modifiedBy),
        deliveryUnit: getRequiredString(styleData.deliveryUnit),
        dimension: getRequiredString(styleData.dimension),
        fit: getRequiredString(styleData.fit),
        licensed: isBoolean(styleData.licensed) ? styleData.licensed : false,
        merchandisingClassification: getRequiredString(styleData.merchandisingClassification),
        segment: getRequiredString(styleData.segment),
        sizeScale: getRequiredString(styleData.sizeScale),
        styleSportActivity: getRequiredString(styleData.styleSportActivity),
        entryIntoMarket: getRequiredString(styleData.entryIntoMarket),
        athlete: getOptionalString(styleData.athlete),
        linkedStyles: isLinkedStylesArray(styleData.linkedStyles)
            ? styleData.linkedStyles.map((linked) => ({
                styleNumber: linked.styleNumber,
                type: linked.type
            }))
            : undefined,
        league: getOptionalString(styleData.league),
        styleGroups: getOptionalStringArray(styleData.styleGroups),
        eventOrMoment: getOptionalString(styleData.eventOrMoment),
        exitFromMarket: getOptionalString(styleData.exitFromMarket)
    };
};
const buildFiltersFromPreferences = async (get) => {
    const userPreferences = await get(userPreferencesAtom);
    const lookups = await get(lookupsAtom);
    const divisionName = userPreferences?.division
        ? lookups?.divisions?.find((d) => String(d.id) === userPreferences.division)
            ?.name
        : undefined;
    return {
        ...initialFilters,
        filters: {
            divisions: divisionName ? [divisionName] : []
        }
    };
};
export const entityCountsCacheAtom = atom({
    project: null,
    model: null,
    style: null,
    expression: null,
    product: null
});
export const modelsDataAtom = atomWithRefresh(async (get) => {
    const filters = await buildFiltersFromPreferences(get);
    const response = await fetchModelsData(filters);
    if (!Array.isArray(response.objects)) {
        log.warn('fetchModelsData returned non-array response:', typeof response.objects);
        return [];
    }
    const transformedData = response.objects.map((model, index) => {
        if (typeof model !== 'object' || model === null) {
            throw new Error(`Invalid model at index ${index}: expected object, got ${typeof model}`);
        }
        const modelData = model;
        return safeTransformModel(modelData);
    });
    return transformedData;
});
export const stylesDataAtom = atomWithRefresh(async (get) => {
    const filters = await buildFiltersFromPreferences(get);
    const response = await fetchStylesData(filters);
    if (!Array.isArray(response.objects)) {
        log.warn('fetchStylesData returned non-array response:', typeof response.objects);
        return [];
    }
    const transformedData = response.objects.map((style, index) => {
        if (typeof style !== 'object' || style === null) {
            throw new Error(`Invalid style at index ${index}: expected object, got ${typeof style}`);
        }
        const styleData = style;
        return safeTransformStyle(styleData);
    });
    return transformedData;
});
export const projectsDataAtom = atomWithRefresh(async () => {
    const response = await fetchProjects();
    if (!Array.isArray(response.objects)) {
        log.warn('fetchProjects returned non-array response:', typeof response.objects);
        return [];
    }
    return response.objects;
});
export const expressionsDataAtom = atomWithRefresh(async (get) => {
    const userPreferences = await get(userPreferencesAtom);
    const queryParams = {
        brandId: userPreferences?.brand || undefined,
        divisionId: userPreferences?.division || undefined,
        fieldOfPlayId: userPreferences?.fieldsOfPlay || undefined
    };
    const response = await fetchExpressions(queryParams);
    if (!Array.isArray(response.objects)) {
        log.warn('fetchExpressions returned non-array response:', typeof response.objects);
        return [];
    }
    return response.objects;
});
const modelsDataAsyncAtom = atom(async (get) => {
    return await get(modelsDataAtom);
});
const stylesDataAsyncAtom = atom(async (get) => {
    return await get(stylesDataAtom);
});
const projectsDataAsyncAtom = atom(async (get) => {
    return await get(projectsDataAtom);
});
const expressionsDataAsyncAtom = atom(async (get) => {
    return await get(expressionsDataAtom);
});
const productsDataAsyncAtom = atom(async (get) => {
    return await get(productsDataAtom);
});
const entityDataAtoms = {
    project: projectsDataAsyncAtom,
    model: modelsDataAsyncAtom,
    style: stylesDataAsyncAtom,
    expression: expressionsDataAsyncAtom,
    product: productsDataAsyncAtom
};
export const gridDataAtom = atom(async (get) => {
    const linePlanEntity = await get(linePlanEntityAtom);
    const dataAtom = entityDataAtoms[linePlanEntity];
    if (!dataAtom) {
        log.warn('Unknown entity type:', linePlanEntity);
        return [];
    }
    const data = await get(dataAtom);
    const size = Array.isArray(data) ? data.length : 0;
    log.info(`${linePlanEntity} data loaded:`, size);
    return data;
});
export const selectedRowsAtom = atom([]);
